import { useState } from 'react'
import { tournamentApi } from '../services/api'

/**
 * Reseller-facing "refund in progress" panel. Shown on a trip (CTQ builder /
 * S&P booking detail) while a suspended refund is working through the provider
 * cancellations. Gives the reseller visibility (status + item checklist) and,
 * while nothing has been cancelled/recovered yet, a "Cancel this refund" action.
 *
 * Props:
 *   pending  the pendingRefund object from the trip GET
 *   base     API base for abort ('/portal/reseller/quotes/:id' or '.../bookings/:id')
 *   onDone   () -> reload the trip after an abort
 *   t        translation fn
 *   canAct   whether the current user may take refund actions (abort / mark
 *            handled). When false the panel is read-only — status + checklist
 *            stay visible, but the action buttons are hidden (guardrail #235).
 */
function fmtMoney(v, cur) {
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const s = '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return cur ? `${cur} ${s}` : s
}

export default function RefundInProgressPanel({ pending, base, onDone, t, canAct = true }) {
  const [busy, setBusy] = useState(false)
  const [recovering, setRecovering] = useState(null) // item id
  const [err, setErr] = useState(null)
  if (!pending) return null

  async function markHandled(item) {
    if (!window.confirm(t('pr_prog_handled_confirm', 'Confirm you\'ve cancelled "{label}" with the course and received your refund? This lets the customer refund proceed.', { label: item.label }))) return
    setRecovering(item.id); setErr(null)
    try {
      const { data } = await tournamentApi.post(`${base}/partial-refund/item/${item.id}/recover`, {})
      if (data.released) window.alert(t('pr_prog_released', 'That was the last step — the customer refund has been issued.'))
      else if (data.releaseError) window.alert(`${data.releaseError}`)
      onDone && onDone()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setRecovering(null) }
  }

  const cur = pending.currency
  const statusLine = pending.status === 'ready'
    ? t('pr_prog_status_ready', 'All provider cancellations are done — the refund is being released to the customer.')
    : t('pr_prog_status_processing', "In progress — we're cancelling the hotel/golf with the providers, then the refund is issued to the customer.")

  async function abort() {
    if (!window.confirm(t('pr_prog_abort_confirm', 'Cancel the {amount} refund? Nothing has been cancelled with the providers yet, so this is safe to undo.', { amount: fmtMoney(pending.amount, cur) }))) return
    setBusy(true); setErr(null)
    try {
      await tournamentApi.post(`${base}/partial-refund/abort`, {})
      window.alert(t('pr_prog_aborted', 'Refund cancelled.'))
      onDone && onDone()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ marginBottom: 12, padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>↩ {t('pr_prog_heading', 'Refund in progress')} · {fmtMoney(pending.amount, cur)}</span>
        {pending.abortable && canAct && (
          <button onClick={abort} disabled={busy}
            style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#b91c1c', borderRadius: 6, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? t('pr_prog_aborting', 'Cancelling…') : t('pr_prog_abort', 'Cancel this refund')}
          </button>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: '#1e40af', marginTop: 4 }}>{statusLine}</div>

      {pending.items && pending.items.length > 0 && (
        <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
          {pending.items.map((it, i) => {
            const done = it.status === 'recovered' || it.status === 'skipped'
            const canHandle = it.owner === 'reseller' && !done && canAct
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 12 }}>
                <span style={{ color: '#1e3a8a', minWidth: 0 }}>
                  {it.type === 'hotel' ? '🏨' : '⛳'} {it.label}
                  {it.owner === 'reseller' && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', padding: '1px 6px', borderRadius: 999 }}>{t('pr_prog_yours', 'yours to cancel')}</span>}
                </span>
                {canHandle ? (
                  <button onClick={() => markHandled(it)} disabled={recovering === it.id}
                    style={{ flexShrink: 0, padding: '4px 10px', fontSize: 11.5, fontWeight: 600, border: 'none', background: recovering === it.id ? '#93c5a3' : '#166534', color: '#fff', borderRadius: 6, cursor: recovering === it.id ? 'default' : 'pointer' }}>
                    {recovering === it.id ? t('pr_prog_saving', 'Saving…') : t('pr_prog_mark_handled', 'Mark as handled')}
                  </button>
                ) : (
                  <span style={{ fontWeight: 700, color: done ? '#166534' : '#b45309', flexShrink: 0 }}>
                    {done ? t('pr_prog_item_done', 'done') : t('pr_prog_item_waiting', 'in progress')}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}

      {err && <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{err}</div>}
      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 6 }}>{t('pr_prog_note', 'The customer has been emailed that their refund is being processed; they get a second email when it completes.')}</div>
    </div>
  )
}
